import React, { useEffect, useRef, useState } from "react";
import Card from "@mui/material/Card";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import { Button, ButtonGroup } from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { useMaterialUIController } from "context";
import CalendarRoot from "./CalendarRoot";
import { application } from "globals/endpoints";
import CTLBaseDialogForm from "application/components/CTLBaseDialogForm";
import CTLNotification from "application/components/CTLNotification";
import { setAssignTo } from "context";
import { Attributes, getColumns, operationURLS } from "./model";
import { getUpdateColumns } from "./model/update";
import { setActivePage } from "context";
import { setTaskDate } from "context";
import { setCurrentChickletData } from "context";
import { setTaskStatus } from "context";

function TaskCalendar({
  onLoad = () => {},
  registerCallBacks,
  updateList,
  setUpdateList,
  pageId,
  searchText,
  pageName,
}) {
  const [controller, dispatch] = useMaterialUIController();
  const { activePage, tableValue, darkMode, taskFileData } = controller;
  const [events, setEvents] = useState([]);
  const [openForm, setOpenForm] = useState(false);
  const [formData, setFormData] = useState({});
  const [currentDateTime, setcurrentDateTime] = useState("");
  const [notify, setNotify] = useState({
    isOpen: false,
    message: "",
    type: "",
    pagename: "",
    status: "",
  });
  const [updateField, setUpdateField] = useState(false);
  const [currentView, setCurrentView] = useState("month");
  const [customDate, setCustomDate] = useState(null);
  const calendarRef = useRef(null);
  const toggleView = (view) => {
    setCurrentView(view);
  };

  useEffect(() => {
    Attributes && onLoad(Attributes);
    registerCallBacks &&
      registerCallBacks({
        openForm: onOpenForm,
      });
  }, [activePage, tableValue]);

  useEffect(() => {
    setCurrentChickletData(dispatch, "");
    setTaskStatus(dispatch, "");
  }, []);

  const fetchTasks = () => {
    application
      .post("/task/listAll")
      .then((res) => {
        setEvents(groupTaskEventsByDate(res.data.data));
      })
      .catch((error) => {
        console.log("Error while fetching tasks: ", error);
      });
  };

  const groupTaskEventsByDate = (tasks) => {
    const groupedTasks = tasks.reduce((acc, task) => {
      const eventDate = new Date(task.assignedOn.replace(" ", "T"))
        .toISOString()
        .split("T")[0];
      if (!acc[eventDate]) {
        acc[eventDate] = [];
      }
      acc[eventDate].push(task);
      return acc;
    }, {});

    return Object.keys(groupedTasks).map((date) => ({
      title: `Total Task: ${groupedTasks[date].length}`,
      start: date,
      end: date,
      className: "event-summary",
      extendedProps: {
        tasks: groupedTasks[date],
      },
    }));
  };

  const formatOnlyDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const handleEventClick = (info) => {
    const eventDate = new Date(info.event?.start);
    const formattedDate = formatOnlyDate(eventDate);
    setCustomDate(formattedDate);
    activePage.key = "taskManagerOnCalender";
    activePage.name = "Task Management";
    activePage.url = "home/app/taskManager";
    activePage.description = "Efficient Task Handling";
    setActivePage(dispatch, activePage);
    setTaskDate(dispatch, formattedDate);
  };

  useEffect(() => {
    fetchTasks();
  }, [updateList]);

  const onOpenForm = (info) => {
    const selectedDate = new Date(info.dateStr);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (selectedDate < today) {
      setNotify({
        isOpen: true,
        status: "You cannot create a task for a past date.",
        type: "error",
        pagename: "Task Management System",
      });
      return;
    }

    const currentTime = new Date().toLocaleTimeString("en-GB", {
      hour: "2-digit",
      minute: "2-digit",
    });
    const formattedDateTime = `${
      selectedDate.toISOString().split("T")[0]
    } ${currentTime}`;

    setcurrentDateTime(formattedDateTime);
    setFormData({
      eventTime: Date.now(),
    });
    setOpenForm(true);
    setUpdateField(false);
  };

  function onCloseForm() {
    setOpenForm(false);
  }

  function formatDate(date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    return `${year}-${month}-${day} ${hours}:${minutes}`;
  }

  function OnCreate(data) {
    const currentDate = new Date();
    currentDate.setHours(0, 0, 0, 0);
    const expectedCompletionDate = data.expectedCompletionDate
      ? new Date(data.expectedCompletionDate)
      : null;

    const assignedOn = currentDateTime ? new Date(currentDateTime) : null;
    if (
      expectedCompletionDate &&
      assignedOn &&
      expectedCompletionDate < assignedOn
    ) {
      setNotify({
        isOpen: true,
        status: "Due date should be after the start date",
        message: "Update cannot be performed",
        type: "error",
        pagename: "Task Management System",
      });
      return;
    }
    const modifiedData = {
      ...data,
      expectedCompletionDate: expectedCompletionDate
        ? formatDate(expectedCompletionDate)
        : null,
      assignedOn: assignedOn ? formatDate(assignedOn) : null,
      currentStats: data.currentStats || "New",
      ...(data.reviewer ? { reviewer: data.reviewer } : {}),
    };
    application
      .post(operationURLS.create, modifiedData)
      .then((response) => {
        if (response?.status === 200 && response?.data?.messageDetail) {
          setUpdateList(Date.now());
          setNotify({
            isOpen: true,
            status: response.data.messageDetail,
            type: "success",
            pagename: "Task Management System",
          });
        }
        setAssignTo(dispatch, "");
      })
      .catch((error) => {
        setNotify({
          isOpen: true,
          status:
            error.response?.data?.message +
            " " +
            error.response?.data?.messageDetail,
          type: "error",
          pagename: "Task Management System",
        });
      });

    setOpenForm(false);
  }

  const onOpenUpdate = (name) => {
    const data = {
      currentPage: 0,
      pageSize: 10,
      sortDirection: "desc",
      sortBy: "createdOn",
      search: "",
      sortDataType: "integer",
      advancedFilters: [
        {
          direction: "from",
          dataType: "string",
          fieldName: "tasksName",
          value: `${name}`,
        },
        {
          direction: "to",
          dataType: "string",
          fieldName: "tasksName",
          value: `${name}`,
        },
      ],
    };

    application
      .post(`/task/list/`, data)
      .then((res) => {
        if (
          res.data &&
          res.data.data &&
          res.data.data.currentPageData.length > 0
        ) {
          setFormData(res.data.data.currentPageData[0]);
        } else {
          setFormData({});
        }
      })
      .catch((error) => {
        console.log("Error while fetching tasks: ", error);
      });
    setFormData({
      eventTime: Date.now(),
    });
    setUpdateField(true);
  };

  const eventContent = (eventInfo) => {
    return (
      <MDBox
        display="flex"
        justifyContent="center"
        alignItems="center"
        sx={{
          padding: "4px 8px",
          borderRadius: "4px",
          border: "1px solid #4fc3f7",
        }}
      >
        <MDTypography
          variant="subtitle2"
          color="error"
          sx={{ fontWeight: "bold" }}
        >
          {eventInfo.event.title}
        </MDTypography>
      </MDBox>
    );
  };

  function onCloseUpdate() {
    setUpdateField(false);
  }

  function onSaveForm(data) {
    const currentDateTime = new Date();
    const changedFields = Object.keys(data).filter(
      (key) => data[key] !== formData[key]
    );
    const isAssignedOnChanged = changedFields.includes("assignedOn");
    const isExpectedCompletionDateChanged = changedFields.includes(
      "expectedCompletionDate"
    );

    if (isAssignedOnChanged || isExpectedCompletionDateChanged) {
      const assignedOnDate = data.assignedOn ? new Date(data.assignedOn) : null;
      const expectedCompletionDate = data.expectedCompletionDate
        ? new Date(data.expectedCompletionDate)
        : null;

      // const today = new Date();
      // today.setHours(0, 0, 0, 0);

      // if (assignedOnDate) {
      //   const assignedDateOnly = new Date(assignedOnDate);
      //   assignedDateOnly.setHours(0, 0, 0, 0);

      //   if (assignedDateOnly < today) {
      //     setNotify({
      //       isOpen: true,
      //       status: "Start Date must be today or a future date.",
      //       type: "error",
      //       pagename: "Task Management System",
      //     });
      //     return;
      //   }
      // }

      if (expectedCompletionDate && expectedCompletionDate <= currentDateTime) {
        setNotify({
          isOpen: true,
          status: "Due Date must be current or future date and time.",
          type: "error",
          pagename: "Task Management System",
        });
        return;
      }
    }
    if (changedFields.length === 0) {
      setNotify({
        isOpen: true,
        status: "No changes detected. Please update some fields to apply.",
        type: "warning",
        pagename: "Task Management System",
      });
      return;
    }
    const fieldNamesMap = {
      assignedOn: "Start Date",
      expectedCompletionDate: "Due Date",
      taskPriority: "Priority",
      currentStats: "Current Status",
      taskDescription: "Description",
      taskType: "TaskType",
      assignedTo: "AssignedTo",
      assignedUser: "AssignedUser",
      reviewer: "reviewer",
    };

    let remarks = changedFields
      .filter((key) => fieldNamesMap[key])
      .map((key) => {
        const friendlyName = fieldNamesMap[key];
        return `${friendlyName} updated from "${formData[key]}" to "${data[key]}"`;
      })
      .join("; ");
    if (data.assignedUser !== formData.assignedUser) {
      remarks += `; Task transferred from User: ${formData.assignedUser} to User: ${data.assignedUser}`;
    }

    const now = new Date();
    const DateTime = `${now.getFullYear()}-${String(
      now.getMonth() + 1
    ).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")} ${String(
      now.getHours()
    ).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`;
    const modifiedData = {
      ...data,
      filePath: taskFileData,
      currentStats:
        data.currentStats === "Closed" ? "Completed" : data.currentStats,
      remarks: remarks,
    };
    application
      .post(operationURLS.update, modifiedData)
      .then((response) => {
        setUpdateList(Date.now());
        setNotify({
          isOpen: true,
          status: response.data.messageDetail,
          type: "success",
          pagename: "Task Management System",
        });
        setUpdateField(false);
      })
      .catch((error) => {
        const errorMessage =
          error.response?.data?.message && error.response?.data?.messageDetail
            ? `${error.response.data.message} ${error.response.data.messageDetail}`
            : "An error occurred";

        setNotify({
          isOpen: true,
          status: errorMessage,
          type: "error",
          pagename: "Task Management System",
        });
      });

    setOpenForm(false);
  }

  const TodayData = () => {
    const currentDate = new Date();
    const formattedDate = currentDate.toISOString().split("T")[0];
    setCurrentView("day");
    activePage.key = "taskManagerOnCalender";
    activePage.name = "Task Management";
    activePage.url = "home/app/taskManager";
    activePage.description = "Efficient Task Handling";
    setActivePage(dispatch, activePage);
    setTaskDate(dispatch, formattedDate);
  };
  return (
    <Card sx={{ height: "100%" }}>
      <MDBox pt={2} px={2} lineHeight={1}>
        <MDTypography
          variant="h6"
          fontWeight="medium"
          textTransform="capitalize"
        >
          Task Calendar
        </MDTypography>
      </MDBox>
      <MDBox display="flex" justifyContent="center" my={2}>
        <Button
          variant={currentView === "month" ? "contained" : "outlined"}
          onClick={() => TodayData("month")}
        >
          Month
        </Button>
        <ButtonGroup>
          <Button
            variant={currentView === "day" ? "contained" : "outlined"}
            onClick={() => TodayData()}
          >
            Today
          </Button>
        </ButtonGroup>
      </MDBox>
      <CalendarRoot p={2} ownerState={{ darkMode }}>
        <FullCalendar
          ref={calendarRef}
          plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
          events={events}
          height="100%"
          dateClick={(info) => onOpenForm(info)}
          eventContent={eventContent}
          eventClick={handleEventClick}
        />
      </CalendarRoot>
      <CTLBaseDialogForm
        entityName="Task Management"
        entityDiscription={formData.tasksName}
        columns={getColumns()}
        openForm={openForm}
        onSaveForm={OnCreate}
        onCloseForm={onCloseForm}
        formData={formData}
        pageAttributes={Attributes}
      />
      {onOpenUpdate && (
        <CTLBaseDialogForm
          entityName="Task Management"
          entityDiscription={formData.tasksName}
          columns={getUpdateColumns()}
          openForm={updateField}
          onSaveForm={onSaveForm}
          onCloseForm={onCloseUpdate}
          formData={formData}
          pageAttributes={Attributes}
        />
      )}

      <CTLNotification notify={notify} setNotify={setNotify} />
    </Card>
  );
}

export default TaskCalendar;
